'use client';

import { useState, useRef, useEffect } from 'react';

interface FormData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  vehicle: string;
  vehicleColor: string;
  service: string;
  location: string;
  howHeard: string;
  message: string;
}

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface CustomSelectProps {
  name: string;
  value: string;
  options: SelectOption[];
  placeholder: string;
  onChange: (name: string, value: string) => void;
  required?: boolean;
  focused: boolean;
  onFocus: () => void;
  onBlur: () => void;
}

function CustomSelect({ name, value, options, placeholder, onChange, required, focused, onFocus, onBlur }: CustomSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? '';

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        onBlur();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onBlur]);

  const triggerClass = `w-full bg-white/5 border rounded-xl px-4 py-3.5 text-left flex items-center justify-between cursor-pointer focus:outline-none transition-all duration-300 text-base ${
    focused || open
      ? 'border-accent/70 bg-accent/5 shadow-[0_0_20px_rgba(220,38,38,0.15)]'
      : 'border-silver-DEFAULT/20 hover:border-silver-DEFAULT/40'
  }`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className={triggerClass}
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) onFocus();
          else onBlur();
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-[rgba(163,159,159,1)]">
          {selectedLabel || placeholder}
        </span>
        <svg
          className={`w-4 h-4 text-silver-DEFAULT/60 transition-transform duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] flex-shrink-0 ${open ? 'rotate-180 text-accent/80' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Animated dropdown panel — max-height slide for smooth jank-free animation */}
      <div
        className="absolute z-50 left-0 right-0 mt-2 rounded-xl border border-white/10 bg-[#0a1020] shadow-[0_24px_64px_rgba(0,0,0,0.7)] overflow-hidden"
        style={{
          maxHeight: open ? '300px' : '0px',
          opacity: open ? 1 : 0,
          transition: open
            ? 'max-height 0.45s cubic-bezier(0.16,1,0.3,1), opacity 0.25s ease'
            : 'max-height 0.35s cubic-bezier(0.4,0,1,1), opacity 0.2s ease',
          pointerEvents: open ? 'auto' : 'none',
          transformOrigin: 'top',
        }}
        role="listbox"
      >
        <div className="py-2 overflow-y-auto" style={{ maxHeight: '296px' }}>
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="option"
              aria-selected={value === opt.value}
              disabled={opt.disabled}
              onClick={() => {
                if (!opt.disabled) {
                  onChange(name, opt.value);
                  setOpen(false);
                  onBlur();
                }
              }}
              className={`group w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 relative overflow-hidden ${
                opt.disabled
                  ? 'text-silver-DEFAULT/30 cursor-default'
                  : value === opt.value
                  ? 'text-white font-semibold' :'text-silver-light cursor-pointer'
              }`}
              style={{
                transition: 'background 0.18s ease, color 0.18s ease',
                background: value === opt.value && !opt.disabled
                  ? 'linear-gradient(90deg, rgba(220,38,38,0.18) 0%, rgba(220,38,38,0.06) 100%)'
                  : 'transparent',
              }}
              onMouseEnter={(e) => {
                if (!opt.disabled && value !== opt.value) {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'linear-gradient(90deg, rgba(220,38,38,0.14) 0%, rgba(220,38,38,0.04) 100%)';
                  (e.currentTarget as HTMLButtonElement).style.color = '#ffffff';
                }
              }}
              onMouseLeave={(e) => {
                if (!opt.disabled && value !== opt.value) {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  (e.currentTarget as HTMLButtonElement).style.color = '';
                }
              }}
            >
              {/* Left accent bar */}
              {!opt.disabled && (
                <span
                  className="absolute left-0 top-0 bottom-0 w-[3px] rounded-r-full"
                  style={{
                    background: value === opt.value
                      ? 'linear-gradient(180deg, #ef4444, #dc2626)'
                      : 'transparent',
                    transition: 'background 0.18s ease',
                    boxShadow: value === opt.value ? '0 0 8px rgba(220,38,38,0.6)' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (value !== opt.value) {
                      (e.currentTarget as HTMLSpanElement).style.background = 'linear-gradient(180deg, rgba(239,68,68,0.5), rgba(220,38,38,0.3))';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (value !== opt.value) {
                      (e.currentTarget as HTMLSpanElement).style.background = 'transparent';
                    }
                  }}
                />
              )}
              {/* Checkmark for selected */}
              {value === opt.value && !opt.disabled ? (
                <svg className="w-3.5 h-3.5 text-accent flex-shrink-0 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <span className="w-3.5 h-3.5 flex-shrink-0 ml-1" />
              )}
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Hidden input for form validation */}
      <input
        type="text"
        name={name}
        value={value}
        required={required}
        readOnly
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  );
}

export default function Contact() {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    vehicle: '',
    vehicleColor: '',
    service: '',
    location: '',
    howHeard: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const inputClass = (name: string) =>
    `w-full bg-white/5 border rounded-xl px-4 py-3.5 text-white placeholder-silver-DEFAULT/50 focus:outline-none transition-all duration-300 text-base ${
      focusedField === name
        ? 'border-accent/70 bg-accent/5 shadow-[0_0_20px_rgba(var(--accent-rgb,220,38,38),0.15)]'
        : 'border-silver-DEFAULT/20 hover:border-silver-DEFAULT/40'
    }`;

  const labelClass = 'block text-silver-light text-sm font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5';

  const serviceOptions: SelectOption[] = [
    { value: 'full-essential', label: 'Full Essential ($80)' },
    { value: 'full-signature', label: 'Full Signature ($235)' },
    { value: 'interior-restoration', label: 'Interior Restoration' },
    { value: 'exterior-pro', label: 'Exterior Pro' },
    { value: 'custom', label: 'Custom / Not Sure' },
  ];

  const howHeardOptions: SelectOption[] = [
    { value: 'instagram', label: 'Instagram' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'google', label: 'Google' },
    { value: 'referral', label: 'Referral' },
  ];

  return (
    <section id="contact" className="relative py-20 sm:py-28 bg-[#050a14] overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-accent/4 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-blue-500/3 rounded-full blur-3xl" />
        {/* Grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }} />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section header */}
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-widest mb-3">
            <span className="w-8 h-px bg-accent/60" />
            Get In Touch
            <span className="w-8 h-px bg-accent/60" />
          </span>
          <h2 className="text-4xl sm:text-5xl font-black text-white mt-2 mb-4">
            Get a <span className="text-accent">Free Quote</span>
          </h2>
          <p className="text-silver-light text-lg max-w-xl mx-auto">
            Ready to transform your vehicle? Fill out the form and we&apos;ll get back to you with a custom quote. We serve all of San Diego County.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* Left: Info */}
          <div className="space-y-8">
            {/* Contact cards */}
            <div className="space-y-4">
              <a
                href="tel:6198838807"
                className="group flex items-center gap-4 p-5 rounded-2xl border border-silver-DEFAULT/15 bg-white/3 hover:bg-accent/8 hover:border-accent/40 transition-all duration-300 hover:shadow-[0_0_30px_rgba(var(--accent-rgb,220,38,38),0.1)]">
                <div className="w-14 h-14 bg-accent/15 border border-accent/30 rounded-2xl flex items-center justify-center group-hover:bg-accent/25 group-hover:scale-110 transition-all duration-300 flex-shrink-0">
                  <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="text-white font-bold text-lg group-hover:text-accent transition-colors">(619) 883-8807</div>
                  <div className="text-white text-sm">Call or Text Anytime</div>
                </div>
                <svg className="w-5 h-5 text-silver-dark group-hover:text-accent group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>

              <div className="flex items-center gap-4 p-5 rounded-2xl border border-silver-DEFAULT/15 bg-white/3">
                <div className="w-14 h-14 bg-accent/15 border border-accent/30 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <div className="text-white font-bold">Serving all of San Diego, CA</div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-silver-DEFAULT/15" />
              <span className="text-silver-DEFAULT/50 text-xs uppercase tracking-widest">Follow Us</span>
              <div className="flex-1 h-px bg-silver-DEFAULT/15" />
            </div>

            {/* Social Links */}
            <div className="grid grid-cols-4 gap-3">
              {[
                {
                  href: 'https://www.instagram.com/figure8detailing',
                  label: 'Instagram',
                  color: 'instagram',
                  iconColor: 'url(#igGrad)',
                  gradientDef: (
                    <defs>
                      <linearGradient id="igGrad" x1="0%" y1="100%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#f09433" />
                        <stop offset="25%" stopColor="#e6683c" />
                        <stop offset="50%" stopColor="#dc2743" />
                        <stop offset="75%" stopColor="#cc2366" />
                        <stop offset="100%" stopColor="#bc1888" />
                      </linearGradient>
                    </defs>
                  ),
                  hoverBg: 'hover:bg-gradient-to-br hover:from-[#f09433]/15 hover:via-[#dc2743]/15 hover:to-[#bc1888]/15',
                  hoverBorder: 'hover:border-[#dc2743]/50',
                  hoverShadow: 'hover:shadow-[0_0_20px_rgba(220,39,67,0.25)]',
                  icon: <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                },
                {
                  href: 'https://www.tiktok.com/@figure8detailing',
                  label: 'TikTok',
                  color: 'tiktok',
                  iconColor: '#ffffff',
                  gradientDef: null,
                  hoverBg: 'hover:bg-[#010101]/40',
                  hoverBorder: 'hover:border-[#69C9D0]/60',
                  hoverShadow: 'hover:shadow-[0_0_20px_rgba(105,201,208,0.25)]',
                  icon: <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.76a4.85 4.85 0 01-1.01-.07z" />
                },
                {
                  href: 'https://www.facebook.com/figure8detailing',
                  label: 'Facebook',
                  color: 'facebook',
                  iconColor: '#1877F2',
                  gradientDef: null,
                  hoverBg: 'hover:bg-[#1877F2]/15',
                  hoverBorder: 'hover:border-[#1877F2]/50',
                  hoverShadow: 'hover:shadow-[0_0_20px_rgba(24,119,242,0.25)]',
                  icon: <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                },
                {
                  href: 'https://www.youtube.com/@figure8detailing',
                  label: 'YouTube',
                  color: 'youtube',
                  iconColor: '#FF0000',
                  gradientDef: null,
                  hoverBg: 'hover:bg-[#FF0000]/15',
                  hoverBorder: 'hover:border-[#FF0000]/50',
                  hoverShadow: 'hover:shadow-[0_0_20px_rgba(255,0,0,0.25)]',
                  icon: <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                }
              ].map(({ href, label, icon, iconColor, gradientDef, hoverBg, hoverBorder, hoverShadow }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group flex flex-col items-center gap-2 p-4 rounded-2xl border border-silver-DEFAULT/15 bg-white/3 ${hoverBg} ${hoverBorder} ${hoverShadow} transition-all duration-300 hover:-translate-y-1.5 hover:scale-105`}>
                  <svg className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" fill={iconColor} viewBox="0 0 24 24">
                    {gradientDef}
                    {icon}
                  </svg>
                  <span className="text-silver-dark group-hover:text-white text-xs font-medium transition-colors duration-300">{label}</span>
                </a>
              ))}
            </div>

            {/* Trust badge */}
            <div className="flex items-center gap-3 p-4 rounded-2xl border border-accent/20 bg-accent/5">
              <div className="w-10 h-10 bg-accent/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <div className="text-white font-semibold text-base">100% Satisfaction Guaranteed</div>
                <div className="text-white text-sm">We&apos;re not done until you&apos;re thrilled</div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="relative" id="quote-form">
            {/* Glow behind form */}
            <div className="absolute -inset-1 bg-gradient-to-br from-accent/20 via-transparent to-accent/10 rounded-3xl blur-xl opacity-60 pointer-events-none" />
            <div className="relative bg-white/5 border border-silver-DEFAULT/20 rounded-2xl p-8 backdrop-blur-sm">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="relative w-20 h-20 mx-auto mb-6">
                    <div className="absolute inset-0 bg-accent/20 rounded-full animate-ping opacity-40" />
                    <div className="relative w-20 h-20 bg-accent/20 border border-accent/40 rounded-full flex items-center justify-center">
                      <svg className="w-9 h-9 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-white font-black text-2xl">Message Sent!</h3>
                  <p className="text-silver-light mb-6">We&apos;ll get back to you shortly with your custom quote.</p>
                  <a
                    href="tel:6198838807"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-accent/15 border border-accent/40 rounded-xl text-accent font-semibold hover:bg-accent/25 transition-all duration-300">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Call us now: (619) 883-8807
                  </a>
                </div>
              ) : (
                <>
                  <div className="mb-7">
                    <h3 className="text-white font-black text-2xl">Request Your Free Quote</h3>
                    <p className="mt-1 text-neutral-50 text-[15px]"></p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5">

                    {/* Step 1: Name */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelClass}>
                          <svg className="w-3.5 h-3.5 text-accent/70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                          First Name
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('firstName')}
                          onBlur={() => setFocusedField(null)}
                          required
                          className={inputClass('firstName')}
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>
                          <svg className="w-3.5 h-3.5 text-accent/70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                          Last Name
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('lastName')}
                          onBlur={() => setFocusedField(null)}
                          required
                          className={inputClass('lastName')}
                          placeholder="Doe"
                        />
                      </div>
                    </div>

                    {/* Step 2: Phone */}
                    <div>
                      <label className={labelClass}>
                        <svg className="w-3.5 h-3.5 text-accent/70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('phone')}
                        onBlur={() => setFocusedField(null)}
                        required
                        className={inputClass('phone')}
                        placeholder="(619) 000-0000"
                      />
                    </div>

                    {/* Step 3: Email (optional) */}
                    <div>
                      <label className={labelClass}>
                        <svg className="w-3.5 h-3.5 text-accent/70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                        Email
                        <span className="ml-1 text-silver-dark/60 normal-case font-normal tracking-normal text-xs">(optional)</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField(null)}
                        className={inputClass('email')}
                        placeholder="john@example.com"
                      />
                    </div>

                    {/* Divider */}
                    <div className="flex items-center gap-3 py-1">
                      <div className="flex-1 h-px bg-silver-DEFAULT/10" />
                      <span className="uppercase tracking-widest text-[rgba(243,242,242,0.8)] text-[13px]">Vehicle Info</span>
                      <div className="flex-1 h-px bg-silver-DEFAULT/10" />
                    </div>

                    {/* Step 4: Vehicle */}
                    <div>
                      <label className={labelClass}>
                        <svg className="w-3.5 h-3.5 text-accent/70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10l2 1h8l2-1zM13 6l2.5 5H19a1 1 0 011 1v3l-1 1h-2" /></svg>
                        Vehicle
                        <span className="ml-1 text-silver-dark/60 normal-case font-normal tracking-normal text-xs">(year, make, model)</span>
                      </label>
                      <input
                        type="text"
                        name="vehicle"
                        value={formData.vehicle}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('vehicle')}
                        onBlur={() => setFocusedField(null)}
                        className={inputClass('vehicle')}
                        placeholder="e.g. 2022 Toyota Camry"
                      />
                    </div>

                    {/* Step 5: Vehicle Color */}
                    <div>
                      <label className={labelClass}>
                        <svg className="w-3.5 h-3.5 text-accent/70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" /></svg>
                        Vehicle Color
                      </label>
                      <input
                        type="text"
                        name="vehicleColor"
                        value={formData.vehicleColor}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('vehicleColor')}
                        onBlur={() => setFocusedField(null)}
                        className={inputClass('vehicleColor')}
                        placeholder="e.g. Blue, Silver"
                      />
                    </div>

                    {/* Divider */}
                    <div className="flex items-center gap-3 py-1">
                      <div className="flex-1 h-px bg-silver-DEFAULT/10" />
                      <span className="uppercase tracking-widest text-[13px] text-[rgba(243,242,242,0.8)]">Service Details</span>
                      <div className="flex-1 h-px bg-silver-DEFAULT/10" />
                    </div>

                    {/* Step 6: Service */}
                    <div>
                      <label className={labelClass}>
                        <svg className="w-3.5 h-3.5 text-accent/70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
                        Service Interested In
                      </label>
                      <CustomSelect
                        name="service"
                        value={formData.service}
                        options={serviceOptions}
                        placeholder="Select a service"
                        onChange={handleSelectChange}
                        required
                        focused={focusedField === 'service'}
                        onFocus={() => setFocusedField('service')}
                        onBlur={() => setFocusedField(null)}
                      />
                    </div>

                    {/* Step 7: Location */}
                    <div>
                      <label className={labelClass}>
                        <svg className="w-3.5 h-3.5 text-accent/70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                        Location
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('location')}
                        onBlur={() => setFocusedField(null)}
                        required
                        className={inputClass('location')}
                        placeholder="e.g. Chula Vista, CA"
                      />
                    </div>

                    {/* Step 8: How Did You Hear */}
                    <div>
                      <label className={labelClass}>
                        <svg className="w-3.5 h-3.5 text-accent/70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        How Did You Hear About Us?
                      </label>
                      <CustomSelect
                        name="howHeard"
                        value={formData.howHeard}
                        options={howHeardOptions}
                        placeholder="Select an option"
                        onChange={handleSelectChange}
                        focused={focusedField === 'howHeard'}
                        onFocus={() => setFocusedField('howHeard')}
                        onBlur={() => setFocusedField(null)}
                      />
                    </div>

                    {/* Step 9: Additional Info (optional) */}
                    <div>
                      <label className={labelClass}>
                        <svg className="w-3.5 h-3.5 text-accent/70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>
                        Additional Info
                        <span className="ml-1 text-silver-dark/60 normal-case font-normal tracking-normal text-xs">(optional)</span>
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('message')}
                        onBlur={() => setFocusedField(null)}
                        rows={3}
                        className={`${inputClass('message')} resize-none`}
                        placeholder="Any concerns, questions or details you would like to share"
                      />
                    </div>

                    <button
                      type="submit"
                      className="group relative w-full overflow-hidden bg-accent hover:bg-accent-dark text-white font-black py-4 rounded-xl text-base transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(var(--accent-rgb,220,38,38),0.4)] active:translate-y-1 active:scale-[0.98] active:shadow-none">
                      <span className="relative z-10 flex items-center justify-center gap-2">
                        Get My Free Quote
                        <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                    </button>

                    <p className="text-center text-silver-dark text-sm">
                      🔒 Your info is private and never shared
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}